import { useState } from 'react';
import { RotateCcw, Undo2 } from 'lucide-react';
import { Button } from '../../../components/ui';

export type Corner = [number, number];

const STEPS = ['top-left', 'top-right', 'bottom-right', 'bottom-left'] as const;

interface Props {
  /** The uploaded photo (object URL). */
  imageUrl: string;
  /** Corners clicked before, to adjust. */
  initial?: Corner[] | null;
  busy?: boolean;
  onSubmit: (corners: Corner[]) => void;
  onCancel: () => void;
}

/**
 * When the reader cannot find the sheet's corner squares, the teacher clicks them on
 * the photo: top-left, top-right, bottom-right, bottom-left (as the sheet is printed,
 * whichever way the photo is turned the reader works out the rest from the QR).
 * Corners are kept as fractions of the photo, so they do not depend on its display size.
 */
export default function CornerPicker({ imageUrl, initial, busy = false, onSubmit, onCancel }: Props) {
  const [corners, setCorners] = useState<Corner[]>(initial?.length === 4 ? initial : []);
  const done = corners.length === 4;

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (done || busy) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    setCorners((c) => [...c, [Number(x.toFixed(4)), Number(y.toFixed(4))]]);
  };

  return (
    <section aria-label="Set the corners" className="space-y-3">
      <div className="rounded-[14px] border border-accent-line bg-accent-bg px-4 py-3 text-[13px] text-warm-ink">
        <p className="font-semibold">The corner squares were not found on this photo.</p>
        <p className="mt-0.5">
          {done
            ? 'All four corners are set. Read the sheet with them, or start over.'
            : <>Click the centre of the <strong>{STEPS[corners.length]}</strong> corner square ({corners.length + 1} of 4).</>}
        </p>
      </div>
      <div className="max-h-[78vh] overflow-auto rounded-[16px] bg-track p-3">
        <div role="presentation" onClick={onClick}
          className={`relative mx-auto max-w-[760px] overflow-hidden rounded-[6px] bg-surface ${done ? '' : 'cursor-crosshair'}`}>
          <img src={imageUrl} alt="Uploaded answer sheet photo" className="block w-full select-none" draggable={false} />
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden>
            {corners.length > 1 && (
              <polygon points={corners.map(([x, y]) => `${x * 100},${y * 100}`).join(' ')}
                className={done ? 'fill-success/10 stroke-success' : 'fill-none stroke-accent'} strokeWidth={0.4} vectorEffect="non-scaling-stroke" />
            )}
          </svg>
          {corners.map(([x, y], i) => (
            <span key={i} style={{ left: `${x * 100}%`, top: `${y * 100}%`, transform: 'translate(-50%, -50%)' }}
              className="pointer-events-none absolute grid h-6 w-6 place-items-center rounded-full border-2 border-white bg-navy font-mono text-[11px] font-semibold text-white shadow-card">
              {i + 1}
            </span>
          ))}
        </div>
      </div>
      <ol aria-label="Corners" className="flex flex-wrap gap-2 text-xs">
        {STEPS.map((s, i) => (
          <li key={s} className={`rounded-full border px-2.5 py-1 font-semibold ${
            i < corners.length ? 'border-success-line bg-success-soft text-success' : i === corners.length ? 'border-accent-line bg-accent-bg text-accent-ink' : 'border-line text-muted'}`}>
            {i + 1}. {s}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" icon={<Undo2 size={14} aria-hidden />} disabled={!corners.length || busy}
          onClick={() => setCorners((c) => c.slice(0, -1))}>Undo</Button>
        <Button variant="ghost" size="sm" icon={<RotateCcw size={14} aria-hidden />} disabled={!corners.length || busy}
          onClick={() => setCorners([])}>Start over</Button>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>Cancel</Button>
        <Button size="sm" disabled={!done} loading={busy} onClick={() => onSubmit(corners)}>Read with these corners</Button>
      </div>
    </section>
  );
}
